import React, { useState } from 'react';
import PageTransition from '../components/ui/PageTransition';
import { motion } from 'framer-motion';
import { 
  BoltIcon, 
  PuzzlePieceIcon, 
  CodeBracketIcon, 
  ChatBubbleLeftRightIcon, 
  ClockIcon, 
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

import {
  BrainIcon,
  BeakerIcon,
  FingerPrintIcon,
  ChartBarIcon,
  UserGroupIcon,
  CogIcon
} from '../components/icons/CustomIcons';

import SignupFormModal from '../components/ui/SignupFormModal';
import Analytics from '../services/analytics';

const features = [
  {
    title: 'AI-Powered Automation',
    description: 'Deploy intelligent AI agents that learn from your processes and automate complex workflows across platforms.',
    icon: <BoltIcon className="w-8 h-8" />,
    color: 'bg-blue-50 text-blue-600',
    details: [
      'Automate repetitive tasks with natural language instructions',
      'AI agents learn from user interactions to improve over time',
      'Handle complex decision trees with ease',
      'Support for Claude, GPT-4, and other advanced AI models'
    ]
  },
  {
    title: 'No-Code Configuration',
    description: 'Create and customize AI agents without writing a single line of code using our intuitive visual interface.',
    icon: <PuzzlePieceIcon className="w-8 h-8" />,
    color: 'bg-green-50 text-green-600',
    details: [
      'Drag-and-drop workflow builder',
      'Visual agent configuration interface',
      'Pre-built templates for common use cases',
      'Customizable without technical knowledge'
    ]
  },
  {
    title: 'Developer API',
    description: 'Advanced customization with our robust API for developers who want to build custom integrations.',
    icon: <CodeBracketIcon className="w-8 h-8" />,
    color: 'bg-purple-50 text-purple-600',
    details: [
      'RESTful API for programmatic control',
      'WebSocket support for real-time interactions',
      'Client libraries for popular languages',
      'Detailed API documentation'
    ]
  },
  {
    title: 'Multi-Channel Communication',
    description: 'Deploy agents across Slack, Discord, WhatsApp, email, and more with consistent behavior.',
    icon: <ChatBubbleLeftRightIcon className="w-8 h-8" />,
    color: 'bg-red-50 text-red-600',
    details: [
      'Unified agent behavior across all channels',
      'Native integrations with popular communication platforms',
      'Consistent user experience regardless of channel',
      'Custom channel adapters available'
    ]
  },
  {
    title: 'Real-Time Execution',
    description: 'Agents respond in real-time to user requests and system events, with minimal latency.',
    icon: <ClockIcon className="w-8 h-8" />,
    color: 'bg-yellow-50 text-yellow-600',
    details: [
      'Sub-second response times for most operations',
      'Asynchronous processing for longer tasks',
      'Event-driven architecture',
      'Websocket connections for instant updates'
    ]
  },
  {
    title: 'Enterprise-Grade Security',
    description: 'SOC 2 compliant with end-to-end encryption and granular permission controls for your data.',
    icon: <ShieldCheckIcon className="w-8 h-8" />,
    color: 'bg-indigo-50 text-indigo-600',
    details: [
      'SOC 2 Type II certified',
      'End-to-end encryption',
      'Role-based access control',
      'Detailed audit logs and compliance reporting'
    ]
  },
  {
    title: 'Advanced AI Capabilities',
    description: 'Leverage cutting-edge AI features like long-term memory, reasoning, planning, and multi-step execution.',
    icon: <BrainIcon className="w-8 h-8" />,
    color: 'bg-pink-50 text-pink-600',
    details: [
      'Long-term memory and context retention',
      'Multi-step reasoning and planning capabilities',
      'Tool use for real-world actions',
      'Chain-of-thought processing'
    ]
  },
  {
    title: 'Continuous Learning',
    description: 'Agents improve over time by learning from interactions and feedback, becoming more personalized and efficient.',
    icon: <BeakerIcon className="w-8 h-8" />,
    color: 'bg-cyan-50 text-cyan-600',
    details: [
      'Feedback loops for continuous improvement',
      'Supervised learning from human corrections',
      'Automated enhancement of agent capabilities',
      'Personal preference adaptation'
    ]
  },
  {
    title: 'Identity and Context Awareness',
    description: 'Agents understand user roles, permissions, and contextual information to provide personalized assistance.',
    icon: <FingerPrintIcon className="w-8 h-8" />,
    color: 'bg-orange-50 text-orange-600',
    details: [
      'User role and permission awareness',
      'Contextual understanding of conversations',
      'Organization-specific knowledge base',
      'Personalized responses based on user history'
    ]
  },
  {
    title: 'Analytics and Insights',
    description: 'Gain valuable insights into agent usage, performance, and areas for optimization.',
    icon: <ChartBarIcon className="w-8 h-8" />,
    color: 'bg-emerald-50 text-emerald-600',
    details: [
      'Comprehensive usage dashboards',
      'Performance metrics and KPIs',
      'Task completion analytics',
      'User satisfaction tracking'
    ]
  },
  {
    title: 'Team Collaboration',
    description: 'Enable seamless collaboration between AI agents and human team members for optimal workflow efficiency.',
    icon: <UserGroupIcon className="w-8 h-8" />,
    color: 'bg-rose-50 text-rose-600',
    details: [
      'Human-in-the-loop workflows',
      'Escalation to human operators when needed',
      'Collaborative task management',
      'Shared knowledge and context'
    ]
  },
  {
    title: 'Customization and Extendability',
    description: 'Adapt agents to your specific requirements with extensive customization options and extendable architecture.',
    icon: <CogIcon className="w-8 h-8" />,
    color: 'bg-teal-50 text-teal-600',
    details: [
      'Custom skill development',
      'Extendable agent capabilities',
      'Integration with proprietary systems',
      'Tailored business logic and rules'
    ]
  },
];

const FeaturesPage: React.FC = () => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const handleStartFreeTrial = () => {
    Analytics.trackButtonClick('Start Your Free Trial', 'features-page');
    // Scroll to the top of the page before showing the modal
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSignupModalOpen(true);
  };

  return (
    <>
      <PageTransition>
        <section className="pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <motion.span 
                className="text-primary-600 font-semibold tracking-wide uppercase text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                All Features
              </motion.span>
              <motion.h1 
                className="mt-2 text-4xl sm:text-5xl font-bold text-gray-900"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Everything You Need for AI Automation
              </motion.h1>
              <motion.p 
                className="mt-4 text-xl text-gray-600"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Discover the full range of capabilities that make AgentSpot the leading platform for deploying AI agents.
              </motion.p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div 
                  key={index}
                  className="bg-white rounded-xl shadow-md overflow-hidden h-full"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    delay: 0.1 * (index % 3) + 0.4,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ 
                    y: -5,
                    boxShadow: "0 15px 30px -5px rgba(0, 0, 0, 0.1)"
                  }}
                >
                  <div className="p-6">
                    <div className={`${feature.color} rounded-lg p-3 inline-flex items-center justify-center mb-4`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-gray-600 mb-4">{feature.description}</p>
                    <ul className="space-y-2 mt-4">
                      {feature.details.map((detail, i) => (
                        <li key={i} className="flex items-start">
                          <svg className="h-5 w-5 text-primary-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              className="mt-16 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <motion.button 
                className="btn btn-primary px-8 py-3 text-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleStartFreeTrial}
              >
                Start Your Free Trial
              </motion.button>
              <p className="mt-4 text-gray-600">No credit card required. 14-day free trial.</p>
            </motion.div>
          </div>
        </section>
      </PageTransition>
      
      {/* Signup Modal - Positioned at root level */}
      <SignupFormModal 
        isOpen={isSignupModalOpen} 
        onClose={() => setIsSignupModalOpen(false)} 
      />
    </>
  );
};

export default FeaturesPage;